import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Check, ChevronDown, ChevronUp, CalendarX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import type { MetricGap } from "@/hooks/useBackfillCheck";

interface BackfillPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gaps: MetricGap[];
  userId: string;
  onGoLog: () => void; // Navigate to Track page to log entries
}

export function BackfillPrompt({
  open,
  onOpenChange,
  gaps,
  userId,
  onGoLog,
}: BackfillPromptProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [expandedMetrics, setExpandedMetrics] = useState<Set<string>>(new Set());

  // Track which (metricKey, date) pairs the user has selected as "no data"
  // Default: all selected (mark all as no data)
  const allPairs = useMemo(() => {
    const pairs: string[] = [];
    for (const gap of gaps) {
      for (const date of gap.gapDates) {
        pairs.push(`${gap.metricKey}_${date}`);
      }
    }
    return pairs;
  }, [gaps]);

  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(
    () => new Set(allPairs)
  );

  const totalGapDays = useMemo(() => {
    const dates = new Set<string>();
    for (const gap of gaps) {
      for (const d of gap.gapDates) {
        dates.add(d);
      }
    }
    return dates.size;
  }, [gaps]);

  const toggleMetricExpanded = useCallback((metricKey: string) => {
    setExpandedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(metricKey)) next.delete(metricKey);
      else next.add(metricKey);
      return next;
    });
  }, []);

  const toggleAllForMetric = useCallback(
    (metricKey: string, gapDates: string[]) => {
      setSelectedPairs((prev) => {
        const next = new Set(prev);
        const allSelected = gapDates.every((d) =>
          next.has(`${metricKey}_${d}`)
        );
        for (const d of gapDates) {
          const key = `${metricKey}_${d}`;
          if (allSelected) next.delete(key);
          else next.add(key);
        }
        return next;
      });
    },
    []
  );

  const togglePair = useCallback((metricKey: string, date: string) => {
    setSelectedPairs((prev) => {
      const next = new Set(prev);
      const key = `${metricKey}_${date}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedPairs(new Set(allPairs));
  }, [allPairs]);

  const deselectAll = useCallback(() => {
    setSelectedPairs(new Set());
  }, []);

  const handleConfirmNoData = async () => {
    if (selectedPairs.size === 0) {
      toast.info(t("backfill.nothing_selected", "No days selected"));
      return;
    }

    setSaving(true);
    try {
      // Build rows to insert
      const rows = Array.from(selectedPairs).map((pair) => {
        const lastUnderscoreIdx = pair.lastIndexOf("_");
        // Format is metricKey_YYYY-MM-DD, but metricKey itself can have underscores
        // So we split on the date pattern
        const dateMatch = pair.match(/_(\d{4}-\d{2}-\d{2})$/);
        if (!dateMatch) {
          throw new Error(`Invalid pair format: ${pair}`);
        }
        const date = dateMatch[1];
        const metricKey = pair.slice(0, pair.length - date.length - 1);
        return {
          user_id: userId,
          entry_date: date,
          metric_key: metricKey,
        };
      });

      // Insert in batches of 100
      const BATCH_SIZE = 100;
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const { error } = await supabase
          .from("no_data_entries" as any)
          .upsert(batch as any, { onConflict: "user_id,entry_date,metric_key" });

        if (error) {
          console.error("[BackfillPrompt] Error saving no_data_entries:", error);
          throw error;
        }
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["backfill-check", userId] });
      queryClient.invalidateQueries({ queryKey: ["no-data-entries", userId] });
      queryClient.invalidateQueries({ queryKey: ["daily-entries", userId] });

      toast.success(
        t("backfill.saved", {
          count: selectedPairs.size,
          defaultValue: `Marked ${selectedPairs.size} entries as no data`,
        })
      );
      onOpenChange(false);
    } catch (err) {
      console.error("[BackfillPrompt] Save error:", err);
      toast.error(t("backfill.save_error", "Failed to save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    // Mark this session as dismissed so we don't re-prompt
    sessionStorage.setItem("backfill_dismissed", "true");
    onOpenChange(false);
  };

  const handleGoLog = () => {
    onOpenChange(false);
    onGoLog();
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="flex items-center gap-2 text-base">
            <CalendarX className="w-5 h-5 text-muted-foreground" />
            {t("backfill.title", "Gaps in your log")}
          </DrawerTitle>
          <DrawerDescription className="text-xs">
            {t(
              "backfill.description",
              {
                count: totalGapDays,
                defaultValue: `We found days with no logged data. Check the ones where you had nothing to track (rest days, off days) — uncheck any you forgot to log so you can go back and enter them.`,
              }
            )}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-2 overflow-y-auto max-h-[50vh]">
          {/* Select all / Deselect all */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={selectedPairs.size === allPairs.length ? deselectAll : selectAll}
              className="text-xs text-primary hover:underline"
            >
              {selectedPairs.size === allPairs.length
                ? t("backfill.deselect_all", "Deselect all")
                : t("backfill.select_all", "Select all")}
            </button>
            <span className="text-xs text-muted-foreground">
              {selectedPairs.size}/{allPairs.length} {t("backfill.selected", "selected")}
            </span>
          </div>

          {/* Metric groups */}
          <div className="space-y-2">
            {gaps.map((gap) => {
              const isExpanded = expandedMetrics.has(gap.metricKey);
              const allSelected = gap.gapDates.every((d) =>
                selectedPairs.has(`${gap.metricKey}_${d}`)
              );
              const someSelected = gap.gapDates.some((d) =>
                selectedPairs.has(`${gap.metricKey}_${d}`)
              );

              return (
                <div
                  key={gap.metricKey}
                  className="rounded-lg border border-border bg-card/50"
                >
                  {/* Metric header row */}
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <Checkbox
                      checked={allSelected}
                      // Show indeterminate state when some but not all selected
                      {...(!allSelected && someSelected ? { "data-state": "indeterminate" } : {})}
                      onCheckedChange={() =>
                        toggleAllForMetric(gap.metricKey, gap.gapDates)
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm flex-1 truncate">
                      {gap.metricEmoji} {gap.metricLabel}
                    </span>
                    <span className="text-xs text-muted-foreground mr-1">
                      {gap.gapDates.length}d
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleMetricExpanded(gap.metricKey)}
                      className="p-1 rounded-md hover:bg-muted/50 transition-colors"
                      aria-label={isExpanded ? "Collapse" : "Expand"}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>

                  {/* Expanded date list */}
                  {isExpanded && (
                    <div className="px-3 pb-2.5 pt-0 border-t border-border/50">
                      <div className="grid grid-cols-2 gap-1.5 mt-2">
                        {gap.gapDates.map((date) => {
                          const pairKey = `${gap.metricKey}_${date}`;
                          const isSelected = selectedPairs.has(pairKey);
                          const parsed = parseISO(date);
                          const displayDate = format(parsed, "EEE, MMM d");

                          return (
                            <label
                              key={date}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/30 cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() =>
                                  togglePair(gap.metricKey, date)
                                }
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-xs text-muted-foreground">
                                {displayDate}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DrawerFooter className="pt-2 pb-4 gap-2" style={{ paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 2rem))' }}>
          {/* Primary action: Mark selected as no data */}
          <Button
            onClick={handleConfirmNoData}
            disabled={saving || selectedPairs.size === 0}
            className="w-full"
            size="sm"
          >
            {saving ? (
              t("backfill.saving", "Saving...")
            ) : (
              <>
                <Check className="w-4 h-4 mr-1.5" />
                {t("backfill.confirm_no_data", {
                  count: selectedPairs.size,
                  defaultValue: `Nothing to log (${selectedPairs.size} days)`,
                })}
              </>
            )}
          </Button>

          {/* Secondary actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleGoLog}
            >
              {t("backfill.go_log", "I forgot — let me log")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={handleDismiss}
            >
              {t("backfill.dismiss", "Not now")}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
